import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getMissions } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { Loader2, Truck, MapPin, Package } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TrackingPage = () => {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMission, setSelectedMission] = useState(null);

  useEffect(() => {
    fetchActiveMissions();
  }, []);

  const fetchActiveMissions = async () => {
    try {
      const data = await getMissions('in_progress');
      // Also get matched missions
      const matchedData = await getMissions('matched');
      setMissions([...data, ...matchedData]);
      if ([...data, ...matchedData].length > 0) {
        setSelectedMission([...data, ...matchedData][0]);
      }
    } catch (error) {
      console.error('Failed to fetch missions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      matched: { label: 'Attribué', className: 'status-matched' },
      in_progress: { label: 'En cours', className: 'status-in_progress' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100' };
    return <Badge className={`badge ${config.className}`}>{config.label}</Badge>;
  };

  const defaultCenter = [46.603354, 1.888334]; // Center of France

  return (
    <Layout>
      <div className="space-y-6" data-testid="tracking-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
            Suivi Temps Réel
          </h1>
          <p className="text-slate-500 mt-1">
            Visualisez vos missions en cours sur la carte
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Map */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-0">
                  <div className="h-[500px] rounded-lg overflow-hidden">
                    <MapContainer 
                      center={selectedMission 
                        ? [(selectedMission.pickup_lat + selectedMission.delivery_lat) / 2, 
                           (selectedMission.pickup_lng + selectedMission.delivery_lng) / 2]
                        : defaultCenter
                      } 
                      zoom={selectedMission ? 6 : 5} 
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap'
                      />
                      {missions.map((mission) => (
                        <div key={mission.id}>
                          <Marker position={[mission.pickup_lat, mission.pickup_lng]}>
                            <Popup>
                              <strong>Enlèvement</strong><br/>
                              {mission.pickup_address}<br/>
                              <em>{mission.title}</em>
                            </Popup>
                          </Marker>
                          <Marker position={[mission.delivery_lat, mission.delivery_lng]}>
                            <Popup>
                              <strong>Livraison</strong><br/>
                              {mission.delivery_address}<br/>
                              <em>{mission.title}</em>
                            </Popup>
                          </Marker>
                          <Polyline 
                            positions={[
                              [mission.pickup_lat, mission.pickup_lng], 
                              [mission.delivery_lat, mission.delivery_lng]
                            ]}
                            color={selectedMission?.id === mission.id ? '#f97316' : '#94a3b8'}
                            weight={selectedMission?.id === mission.id ? 4 : 2}
                            dashArray={selectedMission?.id === mission.id ? undefined : '10, 10'}
                          />
                        </div>
                      ))}
                    </MapContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mission List */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-['Chivo']">
                    Missions actives ({missions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {missions.length > 0 ? (
                    <div className="space-y-3">
                      {missions.map((mission) => (
                        <div
                          key={mission.id}
                          onClick={() => setSelectedMission(mission)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedMission?.id === mission.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`tracking-mission-${mission.id}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3 className="font-medium text-slate-900 text-sm line-clamp-1">
                              {mission.title}
                            </h3>
                            {getStatusBadge(mission.status)}
                          </div>
                          <div className="space-y-1 text-xs text-slate-500">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-emerald-500" />
                              <span className="truncate">{mission.pickup_address}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-red-500" />
                              <span className="truncate">{mission.delivery_address}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Truck className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                      <p className="text-sm text-slate-500">
                        Aucune mission active à suivre
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {selectedMission && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Chivo']">Détails</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div>
                      <span className="text-slate-500">Marchandise:</span>
                      <span className="ml-2 text-slate-900">{selectedMission.cargo_type}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Poids:</span>
                      <span className="ml-2 font-['JetBrains_Mono'] text-slate-900">{selectedMission.weight_kg} kg</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Deadline:</span>
                      <span className="ml-2 font-['JetBrains_Mono'] text-slate-900">
                        {new Date(selectedMission.deadline).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TrackingPage;
